// The covering message, written before the quote goes out.
//
// Craig: "Can I have the ability to add some comments, basically write in the
// body of the email sent with the quote before sending it."
//
// Ported from Proposals' SendLinkModal, cut down to what a quote has: ONE
// recipient (quote.contactEmail — the builder has no recipient list) and one
// message. The backend accepted `emailBodyText` on /api/quotes/:id/send from
// the day the route was built; what was missing was the box to type in.
//
// ⛔ THE BOX IS AN OVERRIDE, NOT A TEMPLATE. It is never pre-filled with the
// default wording. That wording lives in the backend's quoteEmails.js and
// nowhere else — a copy here would be a second source of truth that drifts the
// first time either is edited, and then what Craig previews and what the
// client receives disagree. An EMPTY box sends the server's default, which is
// the exact email every quote so far has gone out with. A written message
// REPLACES the default paragraphs entirely; nothing else in the email moves.
//
// The text is kept on the quote (`emailBodyText`) so a resend opens with what
// was written last time, to edit rather than retype.
import React, { useEffect, useState } from "react";

export default function QuoteSendModal({ quote, busy, onCancel, onSend }) {
  const [body, setBody] = useState("");

  /* Re-seed when the modal opens on a different quote — from that quote's own
     saved message, never from a default. Keyed on id so reopening the same
     quote keeps whatever was typed and abandoned. */
  useEffect(() => {
    if (!quote) return;
    setBody(quote.emailBodyText || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?.id]);

  if (!quote) return null;

  const to = quote.contactEmail || "";
  const who = quote.contactName ? `${quote.contactName} · ${to}` : to;
  const custom = body.trim().length > 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(11,34,64,0.45)", zIndex: 500,
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
         onClick={busy ? undefined : onCancel}>
      <div style={{ background: "var(--s1)", borderRadius: 10, padding: 22, width: 560,
                    maxWidth: "100%", maxHeight: "90vh", overflowY: "auto",
                    border: "1px solid var(--border)" }}
           onClick={(e) => e.stopPropagation()}>
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15,
                      color: "var(--text)", marginBottom: 4 }}>
          Send this quote to {quote.companyName || "the client"}
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 14, lineHeight: 1.6 }}>
          The email carries a link to the quote page and nothing else — no PDF is attached, so
          every open is seen. On the page they can choose options, ask a question, print or
          download a PDF, and accept.
        </div>

        {/* The recipient is SHOWN, not assumed. The old confirm did one thing
            well — it named the address — and that stays, so a quote is never
            sent to the wrong person because the address was off-screen. */}
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)" }}>To</label>
        <div data-testid="quote-send-recipient"
             style={{ fontSize: 12, color: "var(--text)", padding: "6px 8px", marginBottom: 12,
                      background: "var(--s2)", border: "1px solid var(--border)", borderRadius: 6 }}>
          {who}
        </div>

        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)" }}>Your message</label>
        <textarea className="inp" rows={8} value={body}
                  aria-label="Your message"
                  placeholder="Write your covering message, or leave this blank to send the standard one."
                  onChange={(e) => setBody(e.target.value)}
                  disabled={!!busy}
                  style={{ fontSize: 12, lineHeight: 1.6, resize: "vertical" }} />
        <div style={{ fontSize: 10, color: "var(--text3)", marginTop: -6, marginBottom: 12, lineHeight: 1.5 }}>
          {custom
            ? "This replaces the standard covering paragraphs. Blank lines become paragraphs; the branded header, the reference line, the View Quote button and the footer are added around it."
            : "Blank sends the standard covering message — the same wording every quote has gone out with so far. Anything you write here replaces it."}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn" style={{ fontSize: 11 }} onClick={onCancel} disabled={!!busy}>Cancel</button>
          <button className="btn btn-strat" style={{ fontSize: 11 }} disabled={!!busy || !to}
                  onClick={() => onSend(body)}>
            {busy ? "Sending…" : `Send to ${to}`}
          </button>
        </div>
      </div>
    </div>
  );
}
